import { Classes, Dialog, Icon, Intent, PopoverInteractionKind, Tag } from "@blueprintjs/core";
import * as React from "react";
import getConfig from "next/config";
import { Button } from "@blueprintjs/core";
import Router from "next/router";
import FastaDownloadButton from "../fasta-download-button";
import GBKDownloadButton from "../gbk-download-button";
import BedDownloadButton from "../bed-download-button";
import ContigDetailsPopup from "../contig-details";
import {
  ntFormatter,
  datetimeFormatter,
  depthFormatter,
  percentageFormatter,
  renderTagTooltip,
} from "../../utils/metadata-formatter";
import { CollectionName, ContigStatistics, Genome } from "../../api";
import { Popover2 } from "@blueprintjs/popover2";
import { PublicationHistoryPopup } from "../publication-history-popup";
import { getGenomeStats } from "../../utils/qc-statistics";
import Cookie from "js-cookie";
import { DO_NOT_ASK_ABOUT_REDIRECTING_TO_OCX_COOKIE_NAME } from "../../utils/constants";
import RedirectToOCXDialog from "../ocx-redirect-dialog";
import { useContext, useState } from "react";
import { StoresContext } from "../../pages/_app";
import { MetadataCard, MetadataCardsWrapper, MetadataRow } from "../metadata-card";

const { publicRuntimeConfig } = getConfig();
const SEQUENCING_INFO = `
  Our hybrid assembly method leverages highly accurate Illumina short-reads with the scaffolding ability
  of ultra-long Oxford Nanopore reads to generate a genome assembly that is more complete and
  accurate than what could be generated with each individual technology alone.
`;
const ONTQ_SCORE_INFO = `
  Oxford Nanopore reads are generated by a unique chemistry, where an electrical signal is converted to
  basecalls while a single DNA molecule is passed through a biological pore. Although this process results
  in lower Q scores than a typical Illumina read, Oxford Nanopore reads are used for genome scaffolding
  instead of basecalling, and these lower Q scores do not impact assembly quality.
`;
const CONTIG_INFO = `A gold checked circle indicates that the genome is NCBI Complete`;
const ASSEMBLY_INFO = `We follow the assembly level nomenclature used by NCBI to describe each of our assemblies. Assemblies can be one of "Complete genome", "Chromosome", "Scaffold", or "Contig". `;

interface Props {
  genome: Genome;
}

export const GenomeMetadata = (props: Props) => {
  const [contigsOpen, setContigsOpen] = useState<boolean>(false);
  const [historyOpen, setHistoryOpen] = useState<boolean>(false);
  const [runDiscrepancyReportOpen, setRunDiscrepancyReportOpen] = useState<boolean>(false);
  const [youNeedToSubscribeForReportOpen, setYouNeedToSubscribeForReportOpen] =
    useState<boolean>(false);

  const rootStore = useContext(StoresContext);
  const qcStatistics = props.genome.primary_assembly?.derived_metadata?.qc_statistics || undefined;
  const annotationSet = props.genome.primary_assembly?.primary_annotation_set || null;
  const atccMetadata = props.genome.attributes.atcc_metadata || null;

  const renderContigModal = () => {
    if (
      !qcStatistics ||
      !qcStatistics.assembly_statistics ||
      !qcStatistics.assembly_statistics.filtered.contig_statistics
    ) {
      return null;
    }

    return (
      <ContigDetailsPopup
        contigs={qcStatistics.assembly_statistics.filtered.contig_statistics}
        isOpen={contigsOpen}
        close={() => setContigsOpen(false)}
        genomeUuid={props.genome.uuid}
        genomeName={props.genome.name}
      />
    );
  };

  const renderRunDiscrepancyReportModal = () => {
    if (!runDiscrepancyReportOpen) {
      return null;
    }

    return (
      <RedirectToOCXDialog
        onClose={() => setRunDiscrepancyReportOpen(false)}
        url={getRunDiscrepancyReportUrl()}
      />
    );
  };

  const renderYouNeedToSubscribeForReportModal = () => {
    if (!youNeedToSubscribeForReportOpen) {
      return null;
    }

    const handleConfirm = () => {
      Router.push("/plans");
    };

    let secondParagraph = <p>Please select a plan to run your first analysis.</p>;
    if (!rootStore.userStore.isLoggedIn()) {
      secondParagraph = <p>Please log in or select a plan to run your first analysis.</p>;
    }

    return (
      <Dialog
        icon="info-sign"
        isOpen={true}
        usePortal={true}
        title="Subscription Required"
        onClose={() => setYouNeedToSubscribeForReportOpen(false)}
      >
        <div className={Classes.DIALOG_BODY}>
          <p>
            The Discrepancy Report analysis is available as part of the Supporting Membership
            program for the ATCC Genome Portal.
          </p>
          {secondParagraph}
        </div>
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button onClick={() => setYouNeedToSubscribeForReportOpen(false)}>Cancel</Button>
            <Button onClick={handleConfirm} intent={Intent.PRIMARY}>
              View plans
            </Button>
          </div>
        </div>
      </Dialog>
    );
  };

  const openContigs = () => {
    setContigsOpen(true);
    setHistoryOpen(false);
    setRunDiscrepancyReportOpen(false);
  };

  const openHistory = () => {
    setContigsOpen(false);
    setHistoryOpen(true);
    setRunDiscrepancyReportOpen(false);
  };

  const openYouNeedToSubscribeForReportDialog = () => {
    setContigsOpen(false);
    setHistoryOpen(false);
    setRunDiscrepancyReportOpen(false);
    setYouNeedToSubscribeForReportOpen(true);
  };

  const openRunDiscrepancyReportDialog = () => {
    setContigsOpen(false);
    setHistoryOpen(false);
    setRunDiscrepancyReportOpen(true);
  };

  const renderTags = () => {
    const tags = [] as JSX.Element[];

    const genomeTags = props.genome.tags;
    genomeTags.forEach((tag, idx) => {
      const tagKey = `${props.genome.uuid}-${idx}`;
      tags.push(
        <Popover2
          key={tagKey}
          interactionKind={PopoverInteractionKind.HOVER}
          placement="top"
          content={renderTagTooltip(tag.name)}
          popoverClassName="info-tooltip"
        >
          <Tag interactive={true} className="genome-tag">
            {tag.name}
          </Tag>
        </Popover2>
      );
    });

    return tags;
  };

  function getRunDiscrepancyReportUrl() {
    const { uuid } = props.genome;
    const baseOcxUrl = publicRuntimeConfig.SERVER_BASE_URL || "https://app.onecodex.com";
    return `${baseOcxUrl}/atcc/discrepancy-report?genomeUuid=${uuid}`;
  }

  function constructQualityString(collectionName: CollectionName | null): string {
    // All ATCC Bacteria and viruses are currently "Complete"
    // All Fungi are "Scaffold"
    // Otherwise, our completneness threshold is 95

    if (collectionName === "virology") {
      return "Complete";
    }
    if (!qcStatistics) {
      return "";
    }
    return qcStatistics.assembly_level || "";
  }

  const renderSampleMetadata = () => {
    const genomeStats = getGenomeStats(props.genome);
    if (genomeStats?.sequencingStats === undefined) {
      return null;
    }

    const { ont, illumina } = genomeStats.sequencingStats;
    return (
      <MetadataCard className="sample-metadata" title="Input Reads Summary">
        <MetadataRow
          label="Oxford Nanopore Read Count"
          value={(ont?.reads?.total_reads || 0).toLocaleString()}
        />
        <MetadataRow
          label="Oxford Nanopore Median Q Score"
          value={(ont?.reads?.median_quality || 0).toLocaleString()}
          info={ONTQ_SCORE_INFO}
        />
        <MetadataRow
          label="Illumina Read Count"
          value={(illumina?.reads.total_reads || 0).toLocaleString()}
        />
        <MetadataRow
          label="Illumina Mean Coverage Depth"
          value={illumina?.depth?.mean != null ? depthFormatter(illumina.depth.mean) : null}
        />
        <MetadataRow label="Illumina Median Q Score" value={illumina?.reads.median_quality} />
      </MetadataCard>
    );
  };

  const renderAssemblyMetadata = () => {
    const assembly = props.genome.primary_assembly;

    if (!assembly) {
      return null;
    }
    let stats = null;
    if (assembly.derived_metadata.qc_statistics) {
      stats = assembly.derived_metadata.qc_statistics.assembly_statistics;
    }

    const { total_contig_length, contig_statistics, n50, gc_content } = stats?.filtered || {};

    let sequencingTechnology = "Illumina + Oxford Nanopore Hybrid Assembly";
    if (atccMetadata && atccMetadata.sequencing_technology) {
      sequencingTechnology = atccMetadata.sequencing_technology as string;
    }

    let checkMRow = null;
    if (contig_statistics && props.genome.collection_name === "virology") {
      checkMRow = (
        <MetadataRow
          label="Assembly Level"
          info={ASSEMBLY_INFO}
          value={constructQualityString(props.genome.collection_name)}
        />
      );
    }

    return (
      <MetadataCard className="assembly-metadata" title="Assembly Summary">
        <MetadataRow
          label="Date Published"
          value={
            <>
              {datetimeFormatter(props.genome.created_at)}
              <Icon icon="applications" className="popover-icon" />
            </>
          }
          onValueClick={openHistory}
        />
        <MetadataRow
          label="Length"
          value={total_contig_length != null ? ntFormatter(total_contig_length) : null}
        />
        <MetadataRow
          label="Sequencing Technology"
          value={sequencingTechnology}
          info={SEQUENCING_INFO}
        />
        {contig_statistics ? (
          <ContigRow contigStats={contig_statistics} onValueClick={openContigs} />
        ) : null}
        {checkMRow}
        <MetadataRow label="N50" value={n50 != null ? ntFormatter(n50) : null} />
        <MetadataRow
          label="%GC"
          value={gc_content != null ? percentageFormatter(gc_content) : null}
        />
      </MetadataCard>
    );
  };

  const renderGenomeMetadata = () => {
    if (!atccMetadata) {
      return null;
    }

    return (
      <MetadataCard className="genome-metadata" title="Organism Summary">
        <MetadataRow label="Name" value={props.genome.name} />
        {props.genome.attributes.atcc_metadata?.strain_name ? (
          <MetadataRow
            label="Strain Name"
            value={props.genome.attributes.atcc_metadata?.strain_name || ""}
          />
        ) : null}
        <MetadataRow label="Genotype" value={atccMetadata.genotype as string} />
        <MetadataRow label="Isolation" value={atccMetadata.isolation_new_web} />
        <MetadataRow label="Tags" value={renderTags()} />
        <MetadataRow label="Biosafety Level" value={atccMetadata.bsl} />
        {atccMetadata.type_strain !== null && props.genome.collection_name !== "virology" ? (
          <MetadataRow label="Type Strain" value={atccMetadata.type_strain} />
        ) : null}
        <MetadataRow label="Antigenic Properties" value={atccMetadata.antigenic_prop} />
      </MetadataCard>
    );
  };

  const renderAnnotationSetMatadata = () => {
    const genomeStats = getGenomeStats(props.genome);
    if (genomeStats?.annotationStats === undefined) {
      return null;
    }

    const isFungal =
      !!props.genome.collection_name &&
      ["mycology", "protistology"].includes(props.genome.collection_name);
    const proteinString = isFungal ? "Number of BUSCO Annotations" : "Number of CDS";
    const proteinInfo = isFungal
      ? "The annotations listed were generated during quality control using BUSCO, which identifies a selection of universal single-copy orthologs in assembled genomes."
      : "";

    const {
      number_of_16s_rrna,
      number_of_23s_rrna,
      number_of_5s_rrna,
      number_of_cds,
      number_of_hypothetical,
      number_of_trna,
      number_of_busco_annotations,
    } = genomeStats.annotationStats;

    return (
      <MetadataCard className="annotation-set-metadata" title="Annotations Summary">
        <MetadataRow
          label={proteinString}
          value={isFungal ? number_of_busco_annotations : number_of_cds}
          info={proteinInfo}
        />
        {!isFungal ? (
          <>
            <MetadataRow label="Number of Hypothetical Proteins" value={number_of_hypothetical} />
            <MetadataRow label="Number of tRNA" value={number_of_trna} />
            <MetadataRow label="Number of 5s rRNA" value={number_of_5s_rrna} />
            <MetadataRow label="Number of 16s rRNA" value={number_of_16s_rrna} />
            <MetadataRow label="Number of 23s rRNA" value={number_of_23s_rrna} />
          </>
        ) : null}
      </MetadataCard>
    );
  };

  const assemblyUuid = props.genome.primary_assembly!.uuid;
  const annotationSetUuid = annotationSet ? annotationSet.uuid : null;

  return (
    <div className="metadata-page">
      <div className="download-buttons">
        {assemblyUuid ? (
          <FastaDownloadButton
            assemblyUuid={assemblyUuid}
            name={props.genome.name}
            genomeUuid={props.genome.uuid}
            taxonName={props.genome.taxon_name}
          />
        ) : null}
        {annotationSetUuid ? (
          <GBKDownloadButton
            annotationSetUuid={annotationSetUuid}
            assemblyUuid={assemblyUuid}
            name={props.genome.name}
            genomeUuid={props.genome.uuid}
          />
        ) : null}
        {props.genome.epigenome_dataset_uuid !== null ? (
          <BedDownloadButton
            methylationUuid={props.genome.epigenome_dataset_uuid}
            name={props.genome.name}
            genomeUuid={props.genome.uuid}
          />
        ) : null}
        {props.genome.collection_name === "bacteriology" ? (
          <Button
            className="atcc--uppercase"
            onClick={() => {
              if (
                !rootStore.userStore.hasActiveSubscription() ||
                !rootStore.userStore.isLoggedIn()
              ) {
                openYouNeedToSubscribeForReportDialog();
              } else {
                if (Cookie.get(DO_NOT_ASK_ABOUT_REDIRECTING_TO_OCX_COOKIE_NAME)) {
                  window.location.href = getRunDiscrepancyReportUrl();
                } else {
                  openRunDiscrepancyReportDialog();
                }
              }
            }}
            large={true}
            rightIcon={"share"}
            text="Run Discrepancy Report"
          />
        ) : null}
      </div>
      {renderContigModal()}
      {renderRunDiscrepancyReportModal()}
      {renderYouNeedToSubscribeForReportModal()}
      <PublicationHistoryPopup
        close={() => setHistoryOpen(false)}
        isOpen={historyOpen}
        genomeCreatedAt={props.genome.created_at}
        genomeUuid={props.genome.uuid}
      />
      <MetadataCardsWrapper>
        {renderAssemblyMetadata()}
        {renderGenomeMetadata()}
        {renderAnnotationSetMatadata()}
        {renderSampleMetadata()}
      </MetadataCardsWrapper>
    </div>
  );
};

const SPECIALIZED_COLLECTION_NAMES: CollectionName[] = ["mycology", "protistology", "virology"];

function isSpecializedCollection(collectionName: CollectionName | null): boolean {
  return !!collectionName && SPECIALIZED_COLLECTION_NAMES.includes(collectionName);
}

const ContigRow = ({
  contigStats,
  collectionName,
  onValueClick,
}: {
  contigStats: ContigStatistics[];
  collectionName?: CollectionName;
  onValueClick: () => void;
}) => {
  const numCircular = contigStats.filter((contig) => contig.circular).length;
  const genomeIsCircular = numCircular === contigStats.length && numCircular <= 20;
  let repr = `${contigStats.length} (${
    numCircular === contigStats.length ? "All" : numCircular
  } Circularized)`;
  if (collectionName && isSpecializedCollection(collectionName)) {
    repr = `${contigStats.length}`;
  }
  const circularIcon = genomeIsCircular ? (
    <Icon icon="confirm" className="circularized-icon" />
  ) : null;
  const contigNumString =
    collectionName === "virology" ? "Number of Segments" : "Number of Contigs";
  return (
    <MetadataRow
      label={contigNumString}
      info={CONTIG_INFO}
      onValueClick={onValueClick}
      value={
        <>
          {circularIcon}
          {repr}
          <Icon icon="applications" className="popover-icon" />
        </>
      }
    />
  );
};
